import { useState, useEffect, useRef } from "react";
import { normWithMap, isProse } from "./match";
import { fetchAgents, generateDeliverable, perfiles, DE_ENTRADA, type AgentInfo } from "./agents";
import { avatarFor } from "./avatars";
import { callAgent } from "./openrouter";
import { getSelectedText, replaceSelectionTracked, getParagraphTexts, replaceParagraphByText, getDocumentText, applyFixTracked, getDocumentFolder, getDocumentUrl, getPendingChangeTexts, ponerAnchoDelPanel } from "./office";
import { listProject, type ProjFile } from "./project";
import { generateReport, generateGlobalReport, splitPreview, type ReportResult } from "./report";
import { getTelemetry, usd, type Telemetry } from "./telemetry";
import { COMPANION, companionError, logRewrite, registrarDecision, reportPanelError } from "./companion";
import { type PrecioDelCertificado, eurosDelCertificado, fraseDelCertificado, sePuedePagar, rotuloDeHacerOficial, fraseDeOficial, FRASE_PAGO_ABIERTO, FRASE_PAGO_SIN_ABRIR, FRASE_AUN_NO, fraseSinPago, FRASE_DEJO_DE_MIRAR, ESPERA_DEL_PAGO, fraseDeLasEscenas, fraseOficialSinCifra, admiteCodigo, fraseConCodigo, pasoDelCertificado, opacidadDeGenerar, fraseDeTandasSinLeer, llevaCifra } from "./certificadoLogica";
import { fraseDeActivada, FRASE_DE_ACTIVAR, FRASE_DE_CODIGO_ENVIADO, FRASE_SIN_PEDIR, esCorreo, esCodigo, fraseDeCanjear, FRASE_CLAVE_DE_ANTES } from "./activarLogica";
import { clasificar } from "./decision";
import Chat from "./Chat";
import Guion from "./Guion";
import Biblia from "./Biblia";
import Detector from "./Detector";
import Diagnostico from "./Diagnostico";
import ConfirmHost, { askConfirm } from "./Confirm";
import Menu, { PESTANA_DE_ENTRADA, type Pestana, type EstadoDelTablero } from "./Menu";
import CarpetaDelLibro from "./CarpetaDelLibro";
import { rutaLocal, carpetaDe, carpetaDelProyecto, conLaElegida, porQueNoHayTablero, pestanaDeLaVersion } from "./guionLogica";
import { abrirTablero } from "./guionApi";
import Correccion from "./Correccion";
import Reescritura from "./Reescritura";
import AvisoDeVersion from "./AvisoDeVersion";
import Actualizar from "./Actualizar";
import Ritmo from "./Ritmo";
import { CSS_DEL_TEMA, TEMAS, aplicarTema, leerTema, guardarTema, type Tema } from "./tema";

const LS_KEY = "hermes.openrouter.key";
const LS_MODEL = "hermes.model";
const LS_DIR = "hermes.projectdir";
const LS_ELEGIDAS = "hermes.projectdir.elegidas";
/** El ancho del panel que el autor eligió en Ajustes, en píxeles. Solo se guarda si su Word supo ponerlo. */
const LS_ANCHO = "hi.panel.ancho";
/** Hasta el doble del de siempre (Rais: «tanto como la mitad de la ventana, no; quizás el doble de lo que tenemos hoy»). */
export const ANCHOS: readonly [number, string][] = [[350, "Normal"], [525, "Ancho"], [700, "Doble"]];
/** Las carpetas que el autor cargó a mano, por carpeta de documento. Lo que no se pueda leer, como si no hubiera ninguna. */
function carpetasElegidas(): Record<string, string> {
  try { const x = JSON.parse(localStorage.getItem(LS_ELEGIDAS) ?? "{}"); return x && typeof x === "object" && !Array.isArray(x) ? x : {}; } catch { return {}; }
}

function fmtErr(e: any): string {
  const d = e?.debugInfo;
  const dbg = d ? ` [${d.code ?? ""}${d.errorLocation ? " @ " + d.errorLocation : ""}]` : "";
  return `${e?.name ?? "Error"}: ${e?.message ?? String(e)}${dbg}`;
}

const CY = "var(--hi-humano)";
const GD = "var(--hi-oficio)";

export default function App() {
  // La API key vive en el COMPANION (~/.humanink/config.json), no en el navegador.
  // El panel solo sabe si hay key y una pista visual ("sk-or…7f2a").
  const [keyInfo, setKeyInfo] = useState<{ hasKey: boolean; keyHint: string | null; usageOptOut?: boolean } | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const hasKey = !!keyInfo?.hasKey;
  // Roster de colaboradores: llega del companion (los prompts nunca viajan al navegador).
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [agentId, setAgentId] = useState<string>(DE_ENTRADA);
  const agent = agents.find((a) => a.id === agentId);
  const [model, setModel] = useState(
    // Vacío: manda lo asignado en Modelos del Studio (por defecto, Opus 5.5). Un slug aquí pasa por
    // encima de Modelos en cada encargo (equipo rojo 1.6.26: fijaba Sonnet 5 sin que nadie lo eligiera).
    localStorage.getItem(LS_MODEL) ?? "",
  );
  useEffect(() => {
    fetchAgents()
      .then((list) => setAgents(list))
      .catch(() => setAgents([]));
  }, []);
  // Errores del panel no atrapados (Office.js, promesas colgadas) → diagnóstico anónimo de Rais.
  useEffect(() => {
    const onErr = (e: ErrorEvent) => reportPanelError("window", e.error || { message: e.message });
    const onRej = (e: PromiseRejectionEvent) => reportPanelError("unhandledrejection", e.reason);
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);
  const [instruction, setInstruction] = useState("");
  const [scope, setScope] = useState<"selection" | "document">("selection");
  const [status, setStatus] = useState<{ kind: "info" | "err" | "ok"; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // Permite abortar el recorrido del documento entero (cientos de llamadas, dinero real).
  const abort = useRef(false);
  const [showSettings, setShowSettings] = useState(false);
  // AWAP — certificado de autoría (solo bajo el colaborador Auditor).
  const [certBusy, setCertBusy] = useState(false);
  const [cert, setCert] = useState<{ certHash: string; manuscriptHash: string; logHash: string; signedAt: string; verifyUrl: string; title: string; author: string; autoria?: { escenas?: { con?: number; de?: number; fuera?: number; sinContar?: boolean } | null } | null; savedPath?: string | null } | null>(null);
  const [certTitle, setCertTitle] = useState("");
  const [certAuthor, setCertAuthor] = useState("");
  const [certKey, setCertKey] = useState("");
  const [certMsg, setCertMsg] = useState<{ kind: "info" | "err" | "ok"; msg: string } | null>(null);
  const [officialUrl, setOfficialUrl] = useState<string | null>(null);
  // El oficial se paga en Stripe (59 € por título): la cifra la da el servidor, y mientras se paga se espera aquí.
  const [certPrecio, setCertPrecio] = useState<PrecioDelCertificado>(null);
  const [certPago, setCertPago] = useState<{ url: string; desde: number } | null>(null);
  // El código de descuento del certificado, si el autor tiene uno. No se guarda: se escribe cada vez.
  const [certCodigo, setCertCodigo] = useState("");
  const [verCertCodigo, setVerCertCodigo] = useState(false);
  const [certDir, setCertDir] = useState<string | null>(null);
  const [verClaveDeAntes, setVerClaveDeAntes] = useState(false);
  // Pestañas del panel (decisión de producto): Trabajar · Chat · ⚙
  const [tab, setTab] = useState<Pestana>(PESTANA_DE_ENTRADA);
  /** La pestaña del arranque sigue por elegir: si el documento es una versión, abre en la suya, salvo que el autor ya haya pulsado otra. */
  const pestanaPorElegir = useRef(true);

  // El tablero de la novela, desde el menú: lo abre el motor en el navegador, con el libro del documento que
  // hay delante. A Word solo se le pregunta dónde está el documento, y solo al pulsar.
  const [tablero, setTablero] = useState<EstadoDelTablero>(null);
  /** Cambiar de pestaña, desde el menú o desde el «siguiente» de una fase. El aviso del tablero es de donde se pulsó: no se arrastra. */
  const irA = (p: Pestana) => { pestanaPorElegir.current = false; setTab(p); setTablero(null); };
  async function verTablero() {
    setTablero({ estado: "pidiendo" });
    try {
      const url = await getDocumentUrl();
      const ruta = rutaLocal(url);
      if (!ruta) { setTablero({ estado: "error", texto: porQueNoHayTablero(url) }); return; }
      const r = await abrirTablero(carpetaDe(ruta), ruta);
      setTablero({ estado: "abierto", abierta: r.abierta, url: r.url });
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setTablero({ estado: "error", texto: String(err?.message ?? e) });
      if (!err?.codigo) reportPanelError("menu:tablero", e);
    }
  }
  // El aspecto: lo que el autor eligió en Ajustes lo guarda el motor. Sin elegir, el panel sigue oscuro, como siempre.
  const [tema, setTema] = useState<Tema>("oscuro");
  const [temaMsg, setTemaMsg] = useState("");
  useEffect(() => {
    leerTema(COMPANION).then((t) => { const suyo = t ?? "oscuro"; setTema(suyo); aplicarTema(suyo); });
  }, []);
  const elegirTema = async (nuevo: Tema) => {
    setTema(nuevo); aplicarTema(nuevo);
    setTemaMsg((await guardarTema(nuevo, COMPANION)) ? "" : "No he podido guardarlo: se verá así hasta que cierres el panel.");
  };

  // Versiones: la del panel la inyecta Vite; la del companion llega de /health.
  // Si difieren, es que uno de los dos está desactualizado — se muestra para no equivocarnos.
  const [companionVersion, setCompanionVersion] = useState<string | null>(null);
  useEffect(() => {
    fetch(`${COMPANION}/health`)
      .then((r) => r.json())
      .then((j) => setCompanionVersion(j.version ?? null))
      .catch(() => setCompanionVersion(null));
  }, []);

  async function refreshKeyInfo() {
    try {
      const r = await fetch(`${COMPANION}/config`);
      const info = await r.json();
      setKeyInfo(info);
      if (!info.hasKey) {
        setShowSettings(true);
        setTab("ajustes");
      }
    } catch {
      setKeyInfo(null); // companion caído: el estado se verá al primer uso
    }
  }

  useEffect(() => {
    (async () => {
      // Migración: si una versión anterior dejó la key en localStorage, se sube al
      // companion una vez y se borra del navegador.
      const legacy = localStorage.getItem(LS_KEY);
      if (legacy) {
        try {
          await fetch(`${COMPANION}/config`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ openrouter_key: legacy }),
          });
          localStorage.removeItem(LS_KEY);
        } catch {
          /* companion caído: se reintentará en la próxima carga */
        }
      }
      await refreshKeyInfo();
    })();
  }, []);

  // Licencia de HumanInk para Word (pago único, perpetua). El companion revalida contra
  // verify.humanink.io cada 24h; renews_at llega null en las perpetuas y no se muestra.
  const [lic, setLic] = useState<{
    active: boolean; reason?: string; email?: string; renews_at?: number | null; offline?: boolean;
    // Lo manda el companion con cada comprobación de licencia: es de donde sale el aviso de
    // versión nueva. Faltaba en el tipo desde que se añadió el aviso.
    latest?: { version?: string; notes?: string; url?: string } | null;
    // Dónde se compra (lo dice el motor) y por qué vía está activa: la clave de siempre, o el correo.
    comprar?: string; via?: string; plan?: string | null;
  } | null>(null);
  // Activar con el correo, sin claves: se pide un código de seis cifras y se canjea.
  const [actCorreo, setActCorreo] = useState("");
  const [actCodigo, setActCodigo] = useState("");
  const [actPedido, setActPedido] = useState(false);
  const [actOcupado, setActOcupado] = useState(false);
  const [actMsg, setActMsg] = useState<{ bien: boolean; frase: string } | null>(null);

  async function refreshLicense() {
    try {
      // El panel dice quién es: esta petición no lleva `Origin` (es un GET a su mismo sitio) y el motor, sin eso, la tomaría
      // por una del Studio —que es gratis para todos— y contestaría «activa» a quien no ha activado nada.
      const r = await fetch(`${COMPANION}/license/status`, { headers: { "x-hi-cliente": "word" } });
      setLic(await r.json());
    } catch {
      setLic(null);
    }
  }
  useEffect(() => {
    refreshLicense();
  }, []);

  async function pedirCodigo() {
    if (actOcupado || !esCorreo(actCorreo)) return;
    setActOcupado(true); setActMsg(null);
    try {
      const r = await fetch(`${COMPANION}/reclamar`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: actCorreo.trim() }) });
      const j = await r.json().catch(() => null);
      if (r.ok && j && j.ok !== false) { setActPedido(true); setActMsg({ bien: true, frase: FRASE_DE_CODIGO_ENVIADO }); }
      else setActMsg({ bien: false, frase: FRASE_SIN_PEDIR });
    } catch (e: any) {
      setActMsg({ bien: false, frase: companionError(e) });
    } finally { setActOcupado(false); }
  }

  async function activarConCodigo() {
    if (actOcupado || !esCorreo(actCorreo) || !esCodigo(actCodigo)) return;
    setActOcupado(true); setActMsg(null);
    try {
      const r = await fetch(`${COMPANION}/canjear`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: actCorreo.trim(), codigo: actCodigo.trim() }) });
      const f = fraseDeCanjear(await r.json().catch(() => null));
      setActMsg(f);
      if (f.bien) { setActCodigo(""); setActPedido(false); await refreshLicense(); }
    } catch (e: any) {
      setActMsg({ bien: false, frase: companionError(e) });
    } finally { setActOcupado(false); }
  }

  async function saveKeyToCompanion() {
    if (!keyInput.trim()) return;
    try {
      await fetch(`${COMPANION}/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ openrouter_key: keyInput.trim() }),
      });
      setKeyInput("");
      await refreshKeyInfo();
      setStatus({ kind: "ok", msg: "Key guardada en este equipo (companion)." });
    } catch (e: any) {
      setStatus({ kind: "err", msg: "No se pudo guardar la key. " + companionError(e) });
    }
  }

  // Proyecto (Fase 1): carpeta + selector de ficheros de contexto
  const [projectDir, setProjectDir] = useState(localStorage.getItem(LS_DIR) ?? "");
  const [files, setFiles] = useState<ProjFile[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showProject, setShowProject] = useState(false);
  /** En Reescritura, lo de «reescribir lo seleccionado» va plegado: lo primero es el plan del libro. */
  const [conSeleccion, setConSeleccion] = useState(false);
  const [projMsg, setProjMsg] = useState("");

  // La carpeta del manuscrito abierto es la del panel: el chat, los informes y el contexto son los de ESE libro.
  // La recordada solo vale mientras el documento no tenga carpeta (sin guardar, o en la nube).
  const [carpetaDelDocumento, setCarpetaDelDocumento] = useState("");
  const [rutaDelDocumento, setRutaDelDocumento] = useState("");
  /** Al abrir el panel, y cuando la pestaña Biblia pide volver a mirar (un documento recién guardado). */
  const mirarLaCarpeta = () => {
    void getDocumentUrl().then((url) => {
      const f = carpetaDe(url);                       // tal como la da Word: una ruta, un file:// o un https://
      setRutaDelDocumento(rutaLocal(url) ?? "");
      setCarpetaDelDocumento(rutaLocal(f) ?? "");
      const suya = carpetaDelProyecto(f, localStorage.getItem(LS_DIR) ?? "", carpetasElegidas());
      if (suya) { setProjectDir(suya); localStorage.setItem(LS_DIR, suya); }
      // Una versión de corrección o de reescritura abre en su pestaña (Rais, 24-oct-2026). Solo la primera vez, y sin
      // pisar lo que ya mandó: un clic del autor, o Ajustes si falta la llave.
      if (pestanaPorElegir.current) {
        pestanaPorElegir.current = false;
        const p = pestanaDeLaVersion(url);
        if (p) setTab((t) => (t === PESTANA_DE_ENTRADA ? p : t));
      }
    });
  };
  useEffect(mirarLaCarpeta, []);

  // El ancho del panel: se pone al pulsar en Ajustes y, si ese Word supo ponerlo, se recuerda y se vuelve a poner al abrir.
  const [ancho, setAncho] = useState<{ px: number | null; dicho: string }>(() => { let px: number | null = null; try { px = Number(localStorage.getItem(LS_ANCHO)) || null; } catch { /* sin almacenamiento */ } return { px, dicho: "" }; });
  useEffect(() => { if (ancho.px) ponerAnchoDelPanel(ancho.px); }, []);
  function elegirAncho(px: number) {
    const puesto = ponerAnchoDelPanel(px);
    if (puesto) { try { localStorage.setItem(LS_ANCHO, String(px)); } catch { /* no se recordará */ } }
    setAncho({ px: puesto ? px : ancho.px, dicho: puesto
      ? "Hecho. Si no ha cambiado, la ventana de Word es estrecha para ese ancho: Word no deja que el panel pase de la mitad de la ventana."
      : "Este Word no deja cambiar el ancho desde aquí. Arrastra el borde izquierdo del panel." });
  }

  async function loadProject() {
    setProjMsg("Cargando…");
    try {
      localStorage.setItem(LS_DIR, projectDir);
      const { files } = await listProject(projectDir);
      setFiles(files);
      setProjMsg(`${files.length} ficheros`);
      // Cargada a mano y existe: se recuerda para los documentos de esta carpeta (y se olvida si es la suya).
      if (carpetaDelDocumento) localStorage.setItem(LS_ELEGIDAS, JSON.stringify(conLaElegida(carpetasElegidas(), carpetaDelDocumento, projectDir)));
    } catch (e: any) {
      setFiles([]);
      setProjMsg(String(e?.message ?? e));
    }
  }
  function toggleFile(p: string) {
    setSelected((s) => {
      const n = new Set(s);
      n.has(p) ? n.delete(p) : n.add(p);
      return n;
    });
  }

  // Informe de lectura (Fase 2) + arreglos guiados
  const [report, setReport] = useState<ReportResult | null>(null);
  const [reporting, setReporting] = useState(false);
  /** Hay un diagnóstico completo en marcha: ya hace el informe de lectura, y los dos sueltos de debajo se apagan. */
  const [diagnosticando, setDiagnosticando] = useState(false);
  const [applied, setApplied] = useState<Record<number, "ok" | "nf">>({});

  async function runReport() {
    setStatus(null);
    setReport(null);
    setApplied({});
    if (!hasKey) {
      setShowSettings(true);
      setStatus({ kind: "err", msg: "Falta la API key de OpenRouter: guárdala en ⚙ Ajustes." });
      return;
    }
    setReporting(true);
    try {
      const text = await getDocumentText();
      if (text.trim().length < 50) {
        setStatus({ kind: "err", msg: "El documento es muy corto para un informe." });
        return;
      }
      setStatus({ kind: "info", msg: "El Lector está analizando el documento…" });
      const r = await generateReport("", model, text, Array.from(selected), projectDir);
      setReport(r);
      setStatus({
        kind: "ok",
        msg: r.savedPath ? `Informe generado y guardado en ${r.savedPath}` : "Informe generado.",
      });
    } catch (e: any) {
      setStatus({ kind: "err", msg: fmtErr(e) });
      console.error("Hermes report error:", e, e?.debugInfo);
    } finally {
      setReporting(false);
    }
  }
  /** Informe global por capítulos. Cuesta dinero real: se avisa y se pide confirmación. */
  async function runGlobal() {
    setStatus(null);
    setReport(null);
    setApplied({});
    if (!hasKey) {
      setShowSettings(true);
      setStatus({ kind: "err", msg: "Falta la API key de OpenRouter: guárdala en ⚙ Ajustes." });
      return;
    }
    setReporting(true);
    try {
      const text = await getDocumentText();
      setStatus({ kind: "info", msg: "Troceando la novela en capítulos…" });
      const info = await splitPreview(text);
      // Enseñamos los títulos: si el troceado se equivoca, el informe global no vale nada.
      const lista = info.chapters
        .slice(0, 10)
        .map((c) => `  ${c.title} — ${c.palabras.toLocaleString("es-ES")} pal.`)
        .join("\n");
      const mas = info.chapters.length > 10 ? `\n  …y ${info.chapters.length - 10} más` : "";
      const aviso = info.chapters[0]?.title.startsWith("Tramo")
        ? "\n⚠ No he encontrado encabezados de capítulo: troceo por tamaño.\n"
        : "";
      const seguir = await askConfirm(
        `Informe global\n\n` +
          `${info.totalWords.toLocaleString("es-ES")} palabras · ${info.chapters.length} capítulos\n` +
          `${info.calls} llamadas al modelo (una por capítulo + la síntesis).\n` +
          `Puede tardar varios minutos y tiene coste.\n` +
          aviso +
          `\nCapítulos detectados:\n${lista}${mas}\n\n¿Continuar?`,
      );
      if (!seguir) {
        setStatus({ kind: "info", msg: "Informe global cancelado." });
        return;
      }
      setStatus({ kind: "info", msg: `Leyendo ${info.chapters.length} capítulos… (varios minutos)` });
      const r = await generateGlobalReport("", model, text, Array.from(selected), projectDir);
      setReport(r);
      setStatus({
        kind: "ok",
        msg: r.savedPath ? `Informe global guardado en ${r.savedPath}` : "Informe global generado.",
      });
    } catch (e: any) {
      setStatus({ kind: "err", msg: fmtErr(e) });
    } finally {
      setReporting(false);
    }
  }

  async function applyIssue(i: number, issue: { excerpt: string; arreglo: string }) {
    try {
      const ok = await applyFixTracked(issue.excerpt, issue.arreglo);
      if (ok) logRewrite(issue.arreglo);
      setApplied((a) => ({ ...a, [i]: ok ? "ok" : "nf" }));
      return ok;
    } catch {
      setApplied((a) => ({ ...a, [i]: "nf" }));
      return false;
    }
  }
  async function applyAll() {
    const issues = report?.data?.issues ?? [];
    let ok = 0;
    for (let i = 0; i < issues.length; i++) {
      setStatus({ kind: "info", msg: `Aplicando arreglos… ${i + 1}/${issues.length}` });
      if (await applyIssue(i, issues[i])) ok++;
    }
    setStatus({ kind: "ok", msg: `${ok}/${issues.length} arreglos aplicados como control de cambios. Revísalos en Revisar.` });
  }

  // Backoffice de uso (telemetría del companion)
  const [tel, setTel] = useState<Telemetry | null>(null);
  const [showUsage, setShowUsage] = useState(false);
  const [telMsg, setTelMsg] = useState("");

  async function loadTelemetry() {
    setTelMsg("Cargando…");
    try {
      setTel(await getTelemetry());
      setTelMsg("");
    } catch (e: any) {
      setTel(null);
      setTelMsg(String(e?.message ?? e));
    }
  }
  // Refresca el uso al abrir el desplegable y cada vez que termina una llamada.
  useEffect(() => {
    if (showUsage) loadTelemetry();
  }, [showUsage, busy, reporting]);

  function pickAgent(id: string) {
    setAgentId(id);
  }

  async function run() {
    setStatus(null);
    if (!hasKey) {
      setShowSettings(true);
      setStatus({ kind: "err", msg: "Falta la API key de OpenRouter: guárdala en ⚙ Ajustes." });
      return;
    }
    if (!agent) {
      setStatus({ kind: "err", msg: "El roster de colaboradores no ha llegado. " + companionError(null) });
      return;
    }
    setBusy(true);
    try {
      const ctx = Array.from(selected);
      const ctxNote = ctx.length ? ` · ${ctx.length} fichero(s)` : "";

      if (agent.output === "document") {
        // Colaborador que ENTREGA un documento: trabaja sobre el manuscrito completo + contexto,
        // guarda el .docx en la carpeta del proyecto y se abre solo.
        const text = await getDocumentText();
        setStatus({ kind: "info", msg: `${agent.name} está preparando tu entregable…${ctxNote}` });
        const r = await generateDeliverable(agentId, instruction, text, model, ctx, projectDir, rutaDelDocumento || null);
        setStatus({
          kind: "ok",
          // Se mira la EXTENSIÓN antes de prometer Word. Esta línea decía «(abierto en Word)»
          // pasara lo que pasara, así que cuando el documento salía en Markdown —lo que le
          // ocurría a todo el que no tuviera pandoc— el panel lo confirmaba igual y el autor
          // se iba a buscar a Word un fichero que no estaba allí.
          msg: r.savedPath
            ? r.savedPath.endsWith(".docx")
              ? `Entregable de ${agent.name} guardado en ${r.savedPath} (abierto en Word).`
              : `Entregable de ${agent.name} guardado en ${r.savedPath}. No he podido convertirlo a Word, así que va en Markdown: se abre con cualquier editor de texto.`
            : `Entregable generado, pero no se pudo guardar en la carpeta del proyecto.`,
        });
      } else if (scope === "document") {
        const texts = await getParagraphTexts();
        // Un párrafo de prosa tiene varias palabras. Títulos, autoría, códigos y líneas sueltas
        // de la portada no se reescriben: el agente no sabe qué hacer con ellos y los duplica.
        const targets = texts.map((t, i) => ({ t, i })).filter((x) => isProse(x.t));
        if (!targets.length) {
          setStatus({ kind: "err", msg: "El documento no tiene párrafos de prosa que reescribir." });
          return;
        }
        const seguir = await askConfirm(
          `Reescribir el documento entero\n\n` +
            `${targets.length} párrafos de prosa (de ${texts.length} en total).\n` +
            `Se hará 1 llamada al modelo por párrafo: ${targets.length} llamadas.\n` +
            `Tarda varios minutos y tiene coste real.\n\n` +
            `Puedes detenerlo en cualquier momento.\n\n¿Continuar?`,
        );
        if (!seguir) {
          setStatus({ kind: "info", msg: "Cancelado." });
          return;
        }

        abort.current = false;
        let done = 0;
        let skipped = 0;
        let n = 0;
        for (; n < targets.length; n++) {
          if (abort.current) break;
          const { t } = targets[n];
          setStatus({ kind: "info", msg: `${agent!.name}: párrafo ${n + 1}/${targets.length}…${ctxNote}` });
          // Un párrafo que falle no debe abortar los demás: se anota y se sigue.
          try {
            const out = await callAgent(agentId, instruction, t, ctx, model, { saveDir: carpetaDelDocumento, docPath: rutaDelDocumento });
            // Si el agente devuelve lo mismo, no ensuciamos el documento con un cambio vacío.
            if (normWithMap(out).norm === normWithMap(t).norm) skipped++;
            else if (await replaceParagraphByText(t, out)) { logRewrite(out); done++; }
            else skipped++;
          } catch (e) {
            console.warn("Hermes: párrafo omitido", n, e);
            skipped++;
          }
        }
        const parado = abort.current;
        abort.current = false;
        setStatus({
          kind: done ? "ok" : "err",
          msg:
            (parado ? `Detenido en el párrafo ${n} de ${targets.length}. ` : "Listo: ") +
            `${done} párrafos propuestos como control de cambios.` +
            (skipped ? ` ${skipped} sin cambios.` : "") +
            " Revísalos en la pestaña Revisar.",
        });
      } else {
        const selection = await getSelectedText();
        if (!selection.trim()) {
          setStatus({ kind: "err", msg: "Selecciona un fragmento (o cambia el ámbito a Documento entero)." });
          return;
        }
        setStatus({ kind: "info", msg: `${agent!.name} está trabajando…${ctxNote}` });
        const out = await callAgent(agentId, instruction, selection, ctx, model, { saveDir: carpetaDelDocumento, docPath: rutaDelDocumento });
        await replaceSelectionTracked(out.trim());
        logRewrite(out.trim());
        setStatus({
          kind: "ok",
          msg: "Propuesta insertada como control de cambios. Acéptala o recházala en la pestaña Revisar.",
        });
      }
    } catch (e: any) {
      setStatus({ kind: "err", msg: fmtErr(e) });
      console.error("Hermes error:", e, e?.debugInfo);
      reportPanelError("run:" + agentId, e);
    } finally {
      setBusy(false);
    }
  }

  // AWAP: firma el borrador (hashes + PDF) desde el documento actual. El texto solo va al
  // companion local; a verify.humanink.io viajan solo hashes.
  /**
   * Qué hizo el autor con cada propuesta, mirando el documento.
   *
   * Word no avisa cuando alguien acepta un cambio en la cinta Revisar —no existe ese evento en
   * Office.js—, así que se deduce: se le pregunta al motor por las propuestas de este libro que
   * siguen sin decidir y se busca cada una en el texto de ahora.
   *
   * Va JUSTO ANTES de firmar, y no es opcional: un certificado no puede salir con decisiones sin
   * reconciliar. Y lo que se deduce se marca como deducido, nunca como declarado.
   */
  async function reconciliar(cuerpo: string, dir: string | null): Promise<void> {
    try {
      const r = await fetch(`${COMPANION}/bitacora?dir=${encodeURIComponent(dir || "")}&op=reescritura&limite=200`);
      if (!r.ok) return;
      const { ops } = await r.json();
      const pendientesDeWord = await getPendingChangeTexts();
      for (const o of ops as any[]) {
        const propuesta = o?.salida?.texto;
        if (o?.decision || !propuesta) continue;   // ya decidida, o sin texto que buscar
        const v = clasificar(propuesta, cuerpo, pendientesDeWord);
        // Un cambio que el autor aún no ha revisado no se registra: no ha decidido nada.
        if (v.decision === "pendiente") continue;
        registrarDecision(o.id, v.decision, v.similitud, dir, null);
      }
    } catch {
      /* sin reconciliación el certificado sigue saliendo, y lo dirá: por presencia */
    }
  }

  // La cifra del certificado oficial, al abrir su apartado. Un motor anterior no la sabe: entonces no se ofrece pagar.
  useEffect(() => {
    if (tab !== "awap") return;
    let vivo = true;
    fetch(`${COMPANION}/awap/oficial/precio`)
      // Un programa anterior no tiene esta ruta: no es «prueba más tarde», es «actualiza».
      .then((r) => (r.ok ? r.json() : r.status === 404 ? { disponible: false, importe_cent: null, motivo: "motor_viejo" } : null))
      .then((p) => { if (vivo) setCertPrecio(p); }).catch(() => { if (vivo) setCertPrecio(null); });
    return () => { vivo = false; };
  }, [tab]);

  // Mientras hay una página de pago abierta, se pregunta por ella cada pocos segundos; pasado el tope, queda el botón.
  useEffect(() => {
    if (!certPago || !cert || officialUrl) return;
    const t = setInterval(() => {
      if (Date.now() - certPago.desde > ESPERA_DEL_PAGO.tope_ms) { clearInterval(t); setCertMsg({ kind: "info", msg: FRASE_DEJO_DE_MIRAR }); return; }
      void comprobarOficial(true);
    }, ESPERA_DEL_PAGO.cada_ms);
    return () => clearInterval(t);
  }, [certPago, cert, officialUrl]);

  function yaEsOficial(j: { verifyUrl: string; pdf?: string | null }, yaEstaba = false) {
    setOfficialUrl(j.verifyUrl); setCertPago(null);
    setCertMsg({ kind: "ok", msg: fraseDeOficial({ pdf: j.pdf, yaEstaba }) });
  }

  // AWAP: hacer oficial el borrador pagando en Stripe. El motor crea el cobro y abre la página de pago.
  async function hacerOficial() {
    if (!cert) return;
    setCertBusy(true); setCertMsg(null);
    try {
      const r = await fetch(`${COMPANION}/awap/oficial/pedir`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ certHash: cert.certHash, dir: certDir, ...(verCertCodigo && certCodigo.trim() ? { codigo: certCodigo.trim() } : {}) }),
      });
      const j = await r.json();
      if (!r.ok) { setCertMsg({ kind: "err", msg: j.error || "No se ha podido abrir la página de pago. No se ha cobrado nada." }); return; }
      if (j.oficial) { yaEsOficial(j, true); return; }
      setCertPago({ url: j.pago.url, desde: Date.now() });
      setCertMsg({ kind: "info", msg: (j.pago.con_codigo ? fraseConCodigo(j.pago.importe_cent) + " " : "") + (j.pago.abierta ? FRASE_PAGO_ABIERTO : FRASE_PAGO_SIN_ABRIR) });
    } catch (e) { setCertMsg({ kind: "err", msg: companionError(e) }); }
    finally { setCertBusy(false); }
  }

  // ¿Se ha pagado ya? Lo pregunta solo cada pocos segundos y también el botón «Ya he pagado».
  async function comprobarOficial(callado = false) {
    if (!cert) return;
    try {
      const r = await fetch(`${COMPANION}/awap/oficial/estado`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ certHash: cert.certHash, dir: certDir }),
      });
      const j = await r.json();
      if (r.ok && j.oficial) { yaEsOficial(j); return; }
      if (!callado) setCertMsg({ kind: r.ok ? "info" : "err", msg: r.ok ? FRASE_AUN_NO : (j.error || FRASE_AUN_NO) });
    } catch (e) { if (!callado) setCertMsg({ kind: "err", msg: companionError(e) }); }
  }

  async function genDraft() {
    setCertBusy(true); setCertMsg(null); setOfficialUrl(null); setCertPago(null);
    try {
      const text = await getDocumentText();
      if (!text || text.trim().length < 50) {
        setCertMsg({ kind: "err", msg: "El documento está vacío o es muy corto para certificar." });
        return;
      }
      let saveDir: string | null = null;
      try { saveDir = await getDocumentFolder(); } catch { /* sin carpeta: se guarda en el Escritorio */ }
      await reconciliar(text, saveDir);
      setCertDir(saveDir);
      const r = await fetch(`${COMPANION}/awap/draft`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ text, title: certTitle, author: certAuthor, saveDir, doc: rutaDelDocumento || null }),
      });
      const j = await r.json();
      if (!r.ok) { setCertMsg({ kind: "err", msg: j.error || "No se pudo generar el certificado." }); return; }
      setCert(j);
      setCertMsg({
        kind: "ok",
        msg: (j.savedPath ? `Borrador guardado en ${j.savedPath}. ` : "Borrador generado. ") +
          "Aún NO registrado — hazlo oficial para que el QR verifique." +
          // Las tandas de cambios que no constan en él, y cómo arreglarlo (encargo 0005). Un motor anterior no las manda.
          // Que lo de la IA de ellas que está en el texto cuenta, solo si el borrador lleva cifra (encargo 0009).
          (fraseDeTandasSinLeer(j.tandas_sin_leer, llevaCifra(j)) ? ` ${fraseDeTandasSinLeer(j.tandas_sin_leer, llevaCifra(j))}` : ""),
      });
    } catch (e) { setCertMsg({ kind: "err", msg: companionError(e) }); }
    finally { setCertBusy(false); }
  }

  // AWAP: registra el borrador como OFICIAL con una clave de certificado de las que ya se vendieron (el camino de antes).
  async function makeOfficial() {
    if (!cert) return;
    if (!certKey.trim()) { setCertMsg({ kind: "err", msg: "Pega la clave del certificado." }); return; }
    setCertBusy(true); setCertMsg(null);
    try {
      const r = await fetch(`${COMPANION}/awap/official`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({
          certKey: certKey.trim(), certHash: cert.certHash, manuscriptHash: cert.manuscriptHash,
          logHash: cert.logHash, title: cert.title, author: cert.author, signedAt: cert.signedAt, dir: certDir,
        }),
      });
      const j = await r.json();
      if (!r.ok || j.error) {
        const m: Record<string, string> = {
          no_credits: "Esa clave no tiene créditos de certificado disponibles.",
          invalid_license: "Clave no válida — revisa que coincide con el email de tu compra.",
          license_disabled: "Esa licencia está desactivada.",
          bad_request: "Faltan datos del certificado.",
        };
        setCertMsg({ kind: "err", msg: m[j.error] || j.error || "No se pudo registrar el certificado." });
        return;
      }
      setOfficialUrl(j.verifyUrl);
      setCertMsg({ kind: "ok", msg: j.alreadyRegistered ? "Este certificado ya estaba registrado." : "¡Certificado oficial registrado! El QR ya verifica públicamente." });
    } catch (e) { setCertMsg({ kind: "err", msg: companionError(e) }); }
    finally { setCertBusy(false); }
  }

  // Lo que dice el panel tras un encargo o un informe. Lo enseñan las dos pestañas que pueden lanzarlos.
  /** Quién trabaja: el desplegable de colaboradores y su cara. */
  const elColaborador = (<>
      <label style={{ fontSize: 12, color: "var(--hi-tenue)" }}>Colaborador</label>
      <select
        value={agentId}
        onChange={(e) => pickAgent(e.target.value)}
        style={sel}
      >
        {agents.length === 0 && <option value={DE_ENTRADA}>Cargando colaboradores…</option>}
        {perfiles(agents, agentId).map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}{a.output === "document" ? " · entregable" : ""}
          </option>
        ))}
      </select>
      {/* Cara del colaborador elegido — sincronizada con el <select> por agentId */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 12px" }}>
        {avatarFor(agentId) ? (
          <img
            src={avatarFor(agentId)!}
            alt={agent?.name ?? ""}
            style={{ width: 52, height: 52, borderRadius: 12, objectFit: "cover", flex: "none", border: "1px solid var(--hi-raya)" }}
          />
        ) : (
          <div style={{ width: 52, height: 52, borderRadius: 12, background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            {agent?.name?.slice(0, 1) ?? "·"}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{agent?.name ?? "Colaborador"}</div>
          <div style={{ fontSize: 11, color: "var(--hi-tenue)" }}>{agent?.goal ?? "El roster llega del companion."}</div>
        </div>
      </div>
  </>);

  const cajaDeEstado = status && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 8,
            fontSize: 13,
            background: status.kind === "err" ? "var(--hi-mal-fondo)" : status.kind === "ok" ? "var(--hi-bien-fondo)" : "var(--hi-papel)",
            border: `1px solid ${status.kind === "err" ? "var(--hi-mal-raya)" : status.kind === "ok" ? "var(--hi-bien-raya)" : "var(--hi-raya-2)"}`,
          }}
        >
          {status.msg}
        </div>
      );

  return (
    <div style={{ fontFamily: "Segoe UI, system-ui, sans-serif", color: "var(--hi-tinta)", background: "var(--hi-fondo)", minHeight: "100vh", padding: 14, boxSizing: "border-box", overflowX: "hidden", overflowWrap: "anywhere" }}>
      {/* La raíz parte palabras (overflowWrap:anywhere) para que nada desborde el panel.
          La marca es la excepción: nunca se corta, aunque el panel sea estrecho. */}
      <style>{CSS_DEL_TEMA}</style>
      <ConfirmHost />

      <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 20, fontWeight: 800, whiteSpace: "nowrap", overflowWrap: "normal" }}>
          HumanInk<span style={{ color: CY }}> ·</span>
        </span>
        <span style={{ fontSize: 12, color: "var(--hi-tenue)" }}>Writing Coach, dentro de Word</span>
      </div>
      <div style={{ fontSize: 11, color: projectDir ? "var(--hi-tenue)" : "var(--hi-mal)", marginBottom: 12, wordBreak: "break-all" }}>
        Carpeta:{" "}
        {projectDir ? (
          <b style={{ color: "var(--hi-suave)" }}>{projectDir}</b>
        ) : (
          "no detectada — guarda el documento en Word y recarga el panel (el informe iría al Escritorio)"
        )}
      </div>

      {/* Lo primero: si el libro empieza en una carpeta de todo, se le ofrece la suya antes de nada. */}
      <CarpetaDelLibro ruta={rutaDelDocumento} />

      {/* La versión nueva, arriba y a la vista (antes, una línea al final de Ajustes): con muchas versiones seguidas, que se vea. */}
      <Actualizar />
      <Menu tab={tab} alElegir={irA} tablero={tablero} alPedirTablero={() => void verTablero()} />
      {/* Con un Word viejo abierto, en todas las pestañas: lo que se haga aquí no llega a la última (14-oct-2026). En
          Reescritura y en Corrección no: allí ya lo dicen ellas, con su botón. */}
      {tab !== "reescritura" && tab !== "correccion" && <AvisoDeVersion dir={carpetaDelDocumento} doc={rutaDelDocumento} tab={tab} />}

      {/* El colaborador solo se enseña donde trabaja uno: en el chat y, en Reescritura, al proponer cambios sobre lo
          seleccionado (plegado). En el guion escribe el autor (Rais, 9-oct), y los informes y la corrección no dependen
          de a quién se tenga elegido. */}
      {tab === "chat" && elColaborador}

      {tab === "reescritura" && (<>
      {/* EL PLAN DE REESCRITURA (13-oct-2026), lo primero de la pestaña: verlo por fases y aprobarlo. Lo de siempre
          —proponer cambios sobre lo seleccionado— queda debajo, plegado. */}
      <Reescritura dir={carpetaDelDocumento} doc={rutaDelDocumento} />

      <button onClick={() => setConSeleccion((s) => !s)} style={linkBtn} aria-expanded={conSeleccion}>
        Reescribir lo que tengo seleccionado {conSeleccion ? "▲" : "▼"}
      </button>
      {conSeleccion && (<>
      <div style={{ height: 8 }} />
      {elColaborador}
      <button onClick={() => setShowProject((s) => !s)} style={linkBtn}>
        Contexto de proyecto {selected.size ? `· ${selected.size} marcados` : ""} {showProject ? "▲" : "▼"}
      </button>
      {showProject && (
        <div style={{ marginTop: 6, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              value={projectDir}
              onChange={(e) => setProjectDir(e.target.value)}
              placeholder="/ruta/a/tu/proyecto"
              style={{ ...inp, marginBottom: 0, flex: 1 }}
            />
            <button onClick={loadProject} style={smallBtn}>Cargar</button>
          </div>
          {projMsg && <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 4 }}>{projMsg}</div>}
          {files.length > 0 && (
            <div style={{ maxHeight: 160, overflow: "auto", marginTop: 6, border: "1px solid var(--hi-raya)", borderRadius: 8, padding: 6 }}>
              {files.map((f) => (
                <label key={f.path} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, padding: "2px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={selected.has(f.path)} onChange={() => toggleFile(f.path)} />
                  <span style={{ color: "var(--hi-suave)" }}>{f.rel}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 4 }}>
            Marca los ficheros que el agente debe usar (biblia, escaleta, notas…).
          </div>
        </div>
      )}

      <label style={{ display: "block", fontSize: 12, color: "var(--hi-tenue)", marginTop: 8 }}>Instrucción (opcional)</label>
      <textarea
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        placeholder="p. ej. más tensión, frases más cortas…"
        rows={3}
        style={ta}
      />

      {agent?.output !== "document" && (
        <>
          <div style={{ fontSize: 12, color: "var(--hi-tenue)", marginTop: 10 }}>Ámbito</div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            {(["selection", "document"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                style={{ ...scopeBtn, ...(scope === s ? scopeBtnActive : {}) }}
              >
                {s === "selection" ? "Selección" : "Documento entero"}
              </button>
            ))}
          </div>
        </>
      )}

      <button onClick={run} disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
        {busy
          ? "Trabajando…"
          : agent?.output === "document"
            ? `▸ Encargar a ${agent.name}`
            : `▸ Proponer con ${agent?.name ?? "…"}`}
      </button>
      {busy && (
        <button
          onClick={() => {
            abort.current = true;
            setStatus({ kind: "info", msg: "Deteniendo tras el párrafo en curso…" });
          }}
          style={{ ...btn, background: "transparent", color: "var(--hi-mal)", border: "1px solid var(--hi-mal)", marginTop: 6 }}
        >
          ■ Detener
        </button>
      )}

      <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 6 }}>
        {agent?.output === "document"
          ? "Este colaborador ENTREGA un documento: lo guarda en tu carpeta de proyecto y lo abre en Word. Tu manuscrito no se toca."
          : scope === "document"
            ? "Recorre el documento párrafo a párrafo (varias llamadas). Cada cambio va como control de cambios."
            : "Selecciona texto en Word → el agente PROPONE como control de cambios → tú aceptas o rechazas."}
      </div>
      </>)}

      {cajaDeEstado}
      </>)}

      {tab === "informes" && (<>
      {/* EL DIAGNÓSTICO DE UN GOLPE (12-oct-2026), lo primero y lo principal de la pestaña: los seis informes y el Word que
          los junta, con el texto guardado. Los dos informes sueltos de siempre quedan debajo, como secundarios. */}
      <Diagnostico dir={carpetaDelDocumento} doc={rutaDelDocumento} alCorrer={setDiagnosticando} />

      {cajaDeEstado}

      <div style={{ marginTop: 4 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Solo un informe de lectura</div>
        <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginBottom: 6 }}>{diagnosticando
          ? "El diagnóstico completo está en marcha y ya hace este informe: estos dos vuelven cuando termine."
          : "Sueltos, con el texto que tienes ahora en pantalla. El diagnóstico completo, arriba, ya lleva el suyo."}</div>
        <button onClick={runReport} disabled={reporting || diagnosticando} style={{ ...btn, background: CY, marginTop: 0, opacity: reporting || diagnosticando ? 0.6 : 1 }}>
          {reporting ? "Analizando…" : "▸ Generar informe (documento entero)"}
        </button>
        <button
          onClick={runGlobal}
          disabled={reporting || diagnosticando}
          style={{ ...btn, background: "transparent", color: CY, border: `1px solid ${CY}`, marginTop: 6, opacity: reporting || diagnosticando ? 0.6 : 1 }}
        >
          ▸ Informe global (novela por capítulos)
        </button>
        <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 4 }}>
          Para novelas que no caben en una llamada (máx. 120.000 palabras): lee capítulo a capítulo y sintetiza.
          Avisa del coste antes.
        </div>
        {report?.truncated && (
          <div style={{ marginTop: 10, padding: 8, borderRadius: 6, background: "var(--hi-aviso-fondo)", border: "1px solid var(--hi-aviso-raya)", fontSize: 12, color: "var(--hi-aviso-suave)" }}>
            ⚠ Manuscrito de {report.totalWords?.toLocaleString("es-ES")} palabras. Se han analizado solo las primeras{" "}
            {report.analyzedWords?.toLocaleString("es-ES")} (límite de una llamada). Para novela completa hará falta el informe global por capítulos.
          </div>
        )}
        {report?.data && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12 }}>
              {report.data.genero && <span>Género: <b>{report.data.genero}</b></span>}
              {typeof report.data.ppt === "number" && <span>PPT: <b style={{ color: GD }}>{report.data.ppt}</b>/100</span>}
              {typeof report.data.bps === "number" && <span>BPS: <b style={{ color: GD }}>{report.data.bps}</b>/100</span>}
            </div>
            {report.data.scores && (
              <div style={{ fontSize: 12, color: "var(--hi-suave)", marginTop: 4 }}>
                {Object.entries(report.data.scores).map(([k, v]) => `${k} ${v}/10`).join(" · ")}
              </div>
            )}
            {report.data.bps_pilares && (
              <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 2 }}>
                BPS por pilar: {Object.entries(report.data.bps_pilares).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}
              </div>
            )}
            {report.data.resumen && <div style={{ fontSize: 12, color: "var(--hi-tenue)", marginTop: 6 }}>{report.data.resumen}</div>}
            {report.data.issues?.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--hi-tenue)" }}>Problemas detectados:</span>
                  <button onClick={applyAll} style={smallBtn}>Aplicar todos</button>
                </div>
                {report.data.issues.map((iss, i) => (
                  <div key={i} style={{ border: "1px solid var(--hi-raya)", borderRadius: 8, padding: 8, marginBottom: 6, fontSize: 12 }}>
                    <div>
                      <b style={{ color: iss.severidad === "alta" ? "var(--hi-mal)" : "var(--hi-suave)" }}>{iss.severidad}</b> · {iss.problema}
                    </div>
                    <div style={{ color: "var(--hi-tenue)", margin: "4px 0", fontStyle: "italic" }}>“{iss.excerpt}”</div>
                    <button onClick={() => applyIssue(i, iss)} style={smallBtn}>Aplicar arreglo</button>
                    {applied[i] === "ok" && <span style={{ color: "var(--hi-bien)", marginLeft: 8 }}>✓ aplicado</span>}
                    {applied[i] === "nf" && <span style={{ color: "var(--hi-mal)", marginLeft: 8 }}>no encontrado en el texto</span>}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>



      {/* El detector externo sobre el documento abierto, tal como está guardado: también el manuscrito de un cliente. */}
      {rutaDelDocumento
        ? <Detector dir={carpetaDelDocumento} doc={rutaDelDocumento} />
        : <div style={{ fontSize: 12, color: "var(--hi-tenue)", marginTop: 12 }}>Para pasar este documento por el detector externo (Pangram), guárdalo antes en este ordenador.</div>}
      </>)}

      {/* EL CERTIFICADO, EN SU PESTAÑA (11-oct-2026). Antes colgaba de Diagnóstico bajo un colaborador «Auditor» que ya no
          se enseña, y solo se llegaba a él desde un enlace en Ajustes. */}
      {tab === "awap" && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Certificado de autoría (AWAP)</div>
          <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginBottom: 8 }}>{fraseDelCertificado(certPrecio)}</div>
          <input value={certTitle} onChange={(e) => setCertTitle(e.target.value)} placeholder="Título del libro" style={{ ...inp, marginBottom: 6 }} />
          <input value={certAuthor} onChange={(e) => setCertAuthor(e.target.value)} placeholder="Autor/a" style={{ ...inp, marginBottom: 8 }} />
          {/* Sin título o sin autor no se firma: salen impresos en el certificado (el 11-oct salió uno «(sin título) · por -»). */}
          {/* El azul de siempre. Solo se APAGA (más oscuro) mientras el paso que toca es el amarillo, «Hacerlo oficial». */}
          <button onClick={genDraft} disabled={certBusy || !certTitle.trim() || !certAuthor.trim()}
            style={{ ...btn, background: CY, marginTop: 0, opacity: opacidadDeGenerar(pasoDelCertificado(!!cert, !!officialUrl), certBusy || !certTitle.trim() || !certAuthor.trim()) }}>
            {certBusy ? "Firmando…" : "▸ Generar certificado (borrador)"}
          </button>
          {(!certTitle.trim() || !certAuthor.trim()) && (
            <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 4 }}>Escribe el título y tu nombre (o tu seudónimo): salen impresos en el certificado.</div>
          )}

          {cert && (
            <div style={{ marginTop: 10, border: "1px solid var(--hi-raya)", borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 12, color: "var(--hi-suave)" }}>Cert. hash: <code style={{ color: "var(--hi-humano-claro)" }}>{cert.certHash}</code></div>
              {!officialUrl && (
                <>
                  {/* EL OFICIAL SE PAGA EN STRIPE (11-oct-2026): el motor crea el cobro y abre la página de pago; al cobrarse,
                      queda registrado y el PDF oficial aparece junto al borrador. Ya no se manda a Payhip a por una clave. */}
                  {/* Un libro por escenas con alguna sin registro: se dice ANTES de pagar que el oficial queda sin cifra, y si
                      cerrar escenas la traería o no (encargo 0009). */}
                  {fraseDeLasEscenas(cert.autoria) && (
                    <div style={{ fontSize: 12, marginTop: 8, padding: "8px 10px", borderRadius: 6, background: "var(--hi-aviso-fondo)", border: "1px solid var(--hi-aviso-raya)" }}>
                      {fraseDeLasEscenas(cert.autoria)} {fraseOficialSinCifra(cert.autoria)}
                    </div>
                  )}
                  {sePuedePagar(certPrecio) ? (
                    <>
                      <div style={{ fontSize: 12, color: "var(--hi-tenue)", margin: "8px 0 6px" }}>
                        ¿Hacerlo oficial? {eurosDelCertificado(certPrecio)} por título, IVA incluido. Se paga con tarjeta en Stripe y queda registrado al momento.
                      </div>
                      {/* El código de descuento del certificado: solo si el servidor dice que los hay. El de la comunidad no vale aquí. */}
                      {admiteCodigo(certPrecio) && (
                        <div style={{ margin: "0 0 8px" }}>
                          {verCertCodigo
                            ? <input value={certCodigo} onChange={(e) => setCertCodigo(e.target.value)} placeholder="Código de descuento del certificado" autoCapitalize="characters" autoCorrect="off" spellCheck={false} style={{ ...inp, marginBottom: 0 }} />
                            : <button onClick={() => setVerCertCodigo(true)} style={{ ...linkBtn, fontSize: 12 }}>Tengo un código de descuento</button>}
                        </div>
                      )}
                      <button onClick={hacerOficial} disabled={certBusy} style={{ ...btn, background: GD, color: "var(--hi-sobre-acento)", marginTop: 0, opacity: certBusy ? 0.6 : 1 }}>
                        {rotuloDeHacerOficial(certPrecio, certBusy)}
                      </button>
                      {certPago && (
                        <div style={{ fontSize: 12, marginTop: 8 }}>
                          <a href={certPago.url} target="_blank" rel="noopener" style={{ color: CY }}>Abrir la página de pago</a>
                          {" · "}
                          <button onClick={() => comprobarOficial()} style={linkBtn}>Ya he pagado</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--hi-tenue)", margin: "8px 0 4px" }}>{fraseSinPago(certPrecio)}</div>
                  )}
                  {/* Las claves de certificado que ya se vendieron siguen valiendo, en su sitio de siempre y sin estorbar. */}
                  <div style={{ marginTop: 10 }}>
                    <button onClick={() => setVerClaveDeAntes((v) => !v)} style={{ ...linkBtn, color: "var(--hi-apagado)", fontSize: 11 }}>
                      Tengo una clave de certificado de las de antes {verClaveDeAntes ? "▲" : "▼"}
                    </button>
                  </div>
                  {verClaveDeAntes && (
                    <>
                      <input value={certKey} onChange={(e) => setCertKey(e.target.value)} placeholder="Clave del certificado" style={{ ...inp, marginTop: 6, marginBottom: 6 }} />
                      <button onClick={makeOfficial} disabled={certBusy} style={{ ...smallBtn, opacity: certBusy ? 0.6 : 1 }}>
                        {certBusy ? "Registrando…" : "Hacer oficial con la clave"}
                      </button>
                    </>
                  )}
                </>
              )}
              {officialUrl && (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  <div style={{ color: "var(--hi-bien)", fontWeight: 700 }}>✓ Certificado oficial</div>
                  <a href={officialUrl} target="_blank" rel="noopener" style={{ color: CY, wordBreak: "break-all" }}>{officialUrl}</a>
                </div>
              )}
            </div>
          )}

          {certMsg && (
            <div style={{ marginTop: 10, padding: 10, borderRadius: 8, fontSize: 13, wordBreak: "break-word",
              background: certMsg.kind === "err" ? "var(--hi-mal-fondo)" : certMsg.kind === "ok" ? "var(--hi-bien-fondo)" : "var(--hi-papel)",
              border: `1px solid ${certMsg.kind === "err" ? "var(--hi-mal-raya)" : certMsg.kind === "ok" ? "var(--hi-bien-raya)" : "var(--hi-raya-2)"}` }}>
              {certMsg.msg}
            </div>
          )}
        </div>
      )}

      {tab === "biblia" && <Biblia carpeta={carpetaDelDocumento} doc={rutaDelDocumento || null} alMirar={mirarLaCarpeta} alIr={irA} />}
      {/* Guion y Escritura son dos fases del mismo libro: un solo componente, para que pasar de una a otra no pierda la escena. */}
      {(tab === "guion" || tab === "escritura") && <Guion vista={tab} alIr={irA} />}

      {tab === "correccion" && <Correccion />}

      {tab === "chat" && (
        <Chat agent={agent} projectDir={projectDir} model={model} contextFiles={Array.from(selected)} />
      )}

      {tab === "ajustes" && (<>
      <div style={{ margin: "12px 0 4px" }}>
        <div style={{ fontSize: 12, color: "var(--hi-tenue)", marginBottom: 6 }}>Aspecto</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TEMAS.map(([id, nombre]) => (
            <button key={id} onClick={() => void elegirTema(id)} aria-pressed={tema === id} style={{ ...scopeBtn, ...(tema === id ? scopeBtnActive : {}) }}>{nombre}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 4 }}>Vale para este panel y para el tablero de la novela.</div>
        <div style={{ fontWeight: 700, fontSize: 13, margin: "12px 0 6px" }}>Ancho del panel</div>
        <div style={{ display: "flex", gap: 6 }}>
          {ANCHOS.map(([px, nombre]) => (
            <button key={px} onClick={() => elegirAncho(px)} aria-pressed={ancho.px === px}
              style={{ ...smallBtn, flex: 1, fontWeight: ancho.px === px ? 800 : 600, background: ancho.px === px ? "var(--hi-menu-activo)" : "var(--hi-menu)", color: ancho.px === px ? "var(--hi-sobre-acento)" : "var(--hi-tinta)", border: "1px solid var(--hi-raya)" }}>
              {nombre}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 4 }}>{ancho.dicho || "Se recuerda para la próxima vez que abras el panel. También puedes arrastrar su borde izquierdo."}</div>
        {temaMsg && <div style={{ fontSize: 11, color: "var(--hi-aviso)", marginTop: 4 }}>{temaMsg}</div>}
        <Ritmo dir={carpetaDelDocumento} doc={rutaDelDocumento || null} />
      </div>
      <div style={{ marginTop: 6, borderTop: "1px solid transparent", paddingTop: 0 }}>
        <button onClick={() => setShowUsage((s) => !s)} style={linkBtn}>
          ◷ Uso {showUsage ? "▲" : "▼"}
        </button>
        {showUsage && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            {telMsg && <div style={{ color: "var(--hi-mal)" }}>{telMsg}</div>}
            {tel && (
              <>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 8 }}>
                  <span>
                    Hoy: <b style={{ color: GD }}>{usd(tel.today.cost)}</b> · {tel.today.calls} llamadas ·{" "}
                    {tel.today.tokens.toLocaleString("es-ES")} tokens
                  </span>
                  <span style={{ color: "var(--hi-tenue)" }}>
                    Total: {usd(tel.total.cost)} · {tel.total.calls} llamadas
                    {tel.total.errors > 0 && <span style={{ color: "var(--hi-mal)" }}> · {tel.total.errors} errores</span>}
                  </span>
                </div>
                <div style={{ color: "var(--hi-tenue)", marginBottom: 2 }}>Por colaborador:</div>
                {Object.entries(tel.byAgent).map(([a, b]) => (
                  <div key={a} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                    <span>{a}</span>
                    <span style={{ color: "var(--hi-tenue)" }}>
                      {b.calls}× · {usd(b.cost)} · {Math.round(b.ms / Math.max(b.calls, 1) / 1000)}s medio
                    </span>
                  </div>
                ))}
                <div style={{ color: "var(--hi-tenue)", margin: "8px 0 2px" }}>Por modelo:</div>
                {Object.entries(tel.byModel).map(([m, b]) => (
                  <div key={m} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                    <span style={{ overflowWrap: "anywhere" }}>{m}</span>
                    <span style={{ color: "var(--hi-tenue)" }}>
                      {b.calls}× · {usd(b.cost)}
                    </span>
                  </div>
                ))}
                <button onClick={loadTelemetry} style={{ ...smallBtn, marginTop: 8 }}>Actualizar</button>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, borderTop: "1px solid var(--hi-raya)", paddingTop: 10 }}>
        <button onClick={() => setShowSettings((s) => !s)} style={linkBtn}>
          ⚙ Ajustes {showSettings ? "▲" : "▼"}
        </button>
        {showSettings && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Activación</div>
            {lic?.active ? (
              <div style={{ fontSize: 12, marginBottom: 10 }}>
                <span style={{ color: "var(--hi-bien)" }}>{fraseDeActivada(lic)}</span>
                {lic.email && <span style={{ color: "var(--hi-tenue)" }}> · {lic.email}</span>}
                {lic.offline && <span style={{ color: "var(--hi-aviso-suave)" }}> · sin conexión (periodo de gracia)</span>}
              </div>
            ) : (
              <div style={{ marginBottom: 10 }}>
                {lic && lic.reason !== "not_activated" && (
                  <div style={{ fontSize: 12, color: "var(--hi-mal)", marginBottom: 4 }}>
                    {lic.reason === "subscription_cancelled"
                      ? "Licencia desactivada — escríbenos desde humanink.io."
                      : lic.reason === "expired"
                        ? "Licencia caducada — escríbenos desde humanink.io."
                        : lic.reason === "clave_de_antes"
                          ? FRASE_CLAVE_DE_ANTES
                          : `Licencia no activa (${lic.reason}).`}
                  </div>
                )}
                {/* CON EL CORREO, sin claves: es el camino de quien la compra ahora o entra con el código de la comunidad. */}
                <div style={{ fontSize: 12, color: "var(--hi-tenue)", marginBottom: 6 }}>{FRASE_DE_ACTIVAR}</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <input value={actCorreo} onChange={(e) => setActCorreo(e.target.value)} placeholder="tu correo" inputMode="email" style={{ ...inp, marginBottom: 0, flex: 1 }} />
                  <button onClick={pedirCodigo} disabled={actOcupado || !esCorreo(actCorreo)} style={{ ...smallBtn, opacity: !actOcupado && esCorreo(actCorreo) ? 1 : 0.5 }}>
                    {actPedido ? "Otro código" : "Enviarme el código"}
                  </button>
                </div>
                {actPedido && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <input value={actCodigo} onChange={(e) => setActCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="código de 6 cifras" inputMode="numeric" style={{ ...inp, marginBottom: 0, flex: 1, letterSpacing: "0.15em" }} />
                    <button onClick={activarConCodigo} disabled={actOcupado || !esCodigo(actCodigo)} style={{ ...smallBtn, opacity: !actOcupado && esCodigo(actCodigo) ? 1 : 0.5 }}>
                      Activar
                    </button>
                  </div>
                )}
                {actMsg && <div style={{ fontSize: 12, color: actMsg.bien ? "var(--hi-tenue)" : "var(--hi-mal)", marginBottom: 6 }}>{actMsg.frase}</div>}
                <div style={{ fontSize: 12, marginBottom: 10 }}>
                  ¿Aún no la tienes?{" "}
                  <a href={lic?.comprar || "https://verify.humanink.io/app/comprar"} target="_blank" rel="noopener" style={{ color: "var(--hi-info-2)" }}>
                    Cómprala: 99 €, pago único
                  </a>
                </div>
              </div>
            )}
            <label style={{ fontSize: 12, color: "var(--hi-tenue)" }}>OpenRouter API key (BYO · tú pagas tu consumo)</label>
            {hasKey && (
              <div style={{ fontSize: 12, margin: "4px 0" }}>
                ✓ Guardada en este equipo{keyInfo?.keyHint ? <span style={{ color: "var(--hi-tenue)" }}> · {keyInfo.keyHint}</span> : null}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder={hasKey ? "Cambiar la key…" : "sk-or-…"}
                style={{ ...inp, marginBottom: 0, flex: 1 }}
              />
              <button onClick={saveKeyToCompanion} disabled={!keyInput.trim()} style={{ ...smallBtn, opacity: keyInput.trim() ? 1 : 0.5 }}>
                Guardar
              </button>
            </div>
            <label style={{ fontSize: 12, color: "var(--hi-tenue)", display: "block", marginTop: 8 }}>Modelo (slug OpenRouter)</label>
            <input
              value={model}
              onChange={(e) => {
                setModel(e.target.value);
                localStorage.setItem(LS_MODEL, e.target.value);
              }}
              placeholder="Vacío: el que tengas en Modelos (Opus 5.5)"
              style={inp}
            />
            <div style={{ fontSize: 11, color: "var(--hi-apagado)" }}>
              La key se guarda en tu equipo (companion, ~/.humanink) — nunca en el navegador ni en nuestros servidores.
            </div>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14, fontSize: 12, color: "var(--hi-suave)", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!(keyInfo?.usageOptOut)}
                onChange={async (e) => {
                  const optOut = !e.target.checked;
                  try {
                    const r = await fetch(`${COMPANION}/config`, {
                      method: "POST", headers: { "content-type": "application/json" },
                      body: JSON.stringify({ usageOptOut: optOut }),
                    });
                    setKeyInfo(await r.json());
                  } catch { /* companion caído */ }
                }}
                style={{ marginTop: 2 }}
              />
              <span>
                Compartir <b>estadísticas de uso anónimas</b> con HumanInk.
                <span style={{ display: "block", color: "var(--hi-apagado)", fontSize: 11, marginTop: 2 }}>
                  Solo recuentos de qué colaboradores usas — <b>nunca</b> tu texto, tu clave ni los títulos. Puedes desactivarlo aquí.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      </>)}

      {/* El aviso de versión nueva ya no va aquí, en pequeño: está arriba del panel (`Actualizar`), con su botón. */}

      <div style={{ marginTop: 14, paddingTop: 8, borderTop: "1px solid var(--hi-raya)", fontSize: 10, color: "var(--hi-apagado)", display: "flex", justifyContent: "space-between" }}>
        <span>
          panel v{__HI_VERSION__} · motor {companionVersion ? `v${companionVersion}` : "—"}
          {companionVersion && companionVersion !== __HI_VERSION__ && (
            <span style={{ color: "var(--hi-aviso-suave)" }}> · versiones distintas</span>
          )}
        </span>
        <span>
          <a href="https://word.humanink.io/ayuda" target="_blank" rel="noopener" style={{ color: "var(--hi-apagado)" }}>ayuda</a>
          {" · "}
          <a
            href={`https://word.humanink.io/feedback?diag=${encodeURIComponent(`panel v${__HI_VERSION__} · motor ${companionVersion ? "v" + companionVersion : "no responde"} · ${status?.kind === "err" ? status.msg.slice(0, 200) : "sin error visible"}`)}`}
            target="_blank"
            rel="noopener"
            style={{ color: "var(--hi-apagado)" }}
          >
            reportar un problema
          </a>
        </span>
      </div>
    </div>
  );
}

const sel: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, background: "var(--hi-papel)", color: "var(--hi-tinta)", border: "1px solid var(--hi-raya-2)", marginTop: 4 };
const ta: React.CSSProperties = { ...sel, resize: "vertical", fontFamily: "inherit" };
const inp: React.CSSProperties = { ...sel, marginBottom: 8 };
const btn: React.CSSProperties = { width: "100%", boxSizing: "border-box", marginTop: 12, padding: "12px 14px", borderRadius: 10, border: "none", background: GD, color: "var(--hi-sobre-acento)", fontWeight: 800, fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "none", border: "none", color: CY, cursor: "pointer", fontSize: 12, padding: 0 };
const smallBtn: React.CSSProperties = { padding: "8px 12px", borderRadius: 8, border: "none", background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 700, fontSize: 12, cursor: "pointer" };
const scopeBtn: React.CSSProperties = { flex: 1, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--hi-raya-2)", background: "var(--hi-papel)", color: "var(--hi-suave)", fontSize: 12, cursor: "pointer" };
const scopeBtnActive: React.CSSProperties = { background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 700, border: "1px solid var(--hi-humano)" };
